/**
 * Supabase Client
 *
 * Provides Supabase client instances for browser, server, and admin contexts.
 * Gracefully falls back to mock implementations when Supabase is not configured.
 */

import { createBrowserClient, createServerClient } from '@supabase/ssr'
import { createClient as createSupabaseClient } from '@supabase/supabase-js'
import { processLock } from '@supabase/auth-js'

// Check if Supabase is configured
const isSupabaseConfigured = (): boolean => {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

  return Boolean(
    url &&
      anonKey &&
      url !== 'https://your-project.supabase.co' &&
      !url.includes('your-project')
  )
}

/**
 * Get the Supabase URL for server-side operations.
 * Uses SUPABASE_INTERNAL_URL if set (for Docker), otherwise falls back to NEXT_PUBLIC_SUPABASE_URL.
 * This allows server-side code running in Docker to use internal network hostnames.
 */
const getServerSupabaseUrl = (): string => {
  return process.env.SUPABASE_INTERNAL_URL || process.env.NEXT_PUBLIC_SUPABASE_URL || ''
}

/**
 * Get a consistent storage key for auth cookies.
 * IMPORTANT: This must be the same for both browser and server clients.
 * The key is derived from the PUBLIC URL to ensure consistency across environments.
 */
const getStorageKey = (): string => {
  // Always use the public URL for the storage key to ensure browser/server consistency
  const publicUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
  try {
    const hostname = new URL(publicUrl).hostname
    return `sb-${hostname}-auth-token`
  } catch {
    return 'sb-auth-token'
  }
}

// Track warning state
let hasLoggedWarning = false

function logWarningOnce() {
  if (!hasLoggedWarning && process.env.NODE_ENV === 'development') {
    console.warn(
      '[SUPABASE] Running in local development mode - Supabase not configured. ' +
        'Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY to enable.'
    )
    hasLoggedWarning = true
  }
}

// Mock user for local development
const mockUser = {
  id: 'dev-user-123',
  email: 'dev@localhost',
  created_at: new Date().toISOString(),
  app_metadata: {},
  user_metadata: {},
  aud: 'authenticated',
}

// Mock session for local development
const mockSession = {
  access_token: 'mock-access-token',
  refresh_token: 'mock-refresh-token',
  expires_at: Date.now() + 3600000,
  expires_in: 3600,
  token_type: 'bearer',
  user: mockUser,
}

/**
 * Create a chainable query builder for the stub client
 * Supports all common Supabase query methods and returns empty data
 */
const createStubQueryBuilder = () => {
  const builder: Record<string, unknown> = {
    // Terminal methods (return data)
    single: async () => ({ data: null, error: null }),
    then: async (resolve: (arg0: { data: never[]; error: null }) => unknown) =>
      resolve({ data: [], error: null }),
    // Chainable filter methods
    eq: () => builder,
    neq: () => builder,
    gt: () => builder,
    gte: () => builder,
    lt: () => builder,
    lte: () => builder,
    like: () => builder,
    ilike: () => builder,
    is: () => builder,
    in: () => builder,
    contains: () => builder,
    containedBy: () => builder,
    not: () => builder,
    or: () => builder,
    filter: () => builder,
    match: () => builder,
    // Chainable modifier methods
    order: () => builder,
    limit: () => builder,
    range: () => builder,
    select: () => builder,
  }
  return builder
}

/**
 * Stub client for local development without Supabase
 */
const createStubClient = () => ({
  auth: {
    getUser: async () => ({ data: { user: mockUser }, error: null }),
    getSession: async () => ({ data: { session: mockSession }, error: null }),
    signInWithPassword: async ({ email }: { email: string }) => {
      console.warn('[SUPABASE STUB] Auto-login with mock user for local development')
      return {
        data: {
          user: { ...mockUser, email },
          session: mockSession,
        },
        error: null,
      }
    },
    signUp: async ({ email }: { email: string }) => {
      console.warn('[SUPABASE STUB] Mock signup for local development')
      return {
        data: {
          user: { ...mockUser, email },
          session: mockSession,
        },
        error: null,
      }
    },
    signOut: async () => ({ error: null }),
    resetPasswordForEmail: async () => ({ data: {}, error: null }),
    updateUser: async () => ({ data: { user: mockUser }, error: null }),
    onAuthStateChange: () => ({
      data: { subscription: { unsubscribe: () => {} } },
    }),
    admin: {
      createUser: async () => ({
        data: { user: null },
        error: { message: 'Supabase not configured for admin operations' },
      }),
      deleteUser: async () => ({
        error: { message: 'Supabase not configured for admin operations' },
      }),
      listUsers: async () => ({ data: { users: [] }, error: null }),
    },
  },
  from: (_table: string) => ({
    select: (_columns?: string) => createStubQueryBuilder(),
    insert: (_data: unknown) => ({
      select: () => ({
        single: async () => ({
          data: null,
          error: { message: 'Supabase not configured' },
        }),
      }),
      then: async (resolve: (arg0: { data: null; error: { message: string } }) => unknown) =>
        resolve({ data: null, error: { message: 'Supabase not configured' } }),
    }),
    update: (_data: unknown) => {
      // Create chainable query builder for update operations
      const updateBuilder: Record<string, unknown> = {
        // Support chained .eq() calls
        eq: () => updateBuilder,
        neq: () => updateBuilder,
        in: () => updateBuilder,
        is: () => updateBuilder,
        // Terminal methods
        select: () => ({
          single: async () => ({
            data: null,
            error: { message: 'Supabase not configured' },
          }),
          then: async (resolve: (arg0: { data: never[]; error: { message: string } }) => unknown) =>
            resolve({ data: [], error: { message: 'Supabase not configured' } }),
        }),
        // Direct resolution (update without returning data)
        then: async (resolve: (arg0: { data: null; error: { message: string } }) => unknown) =>
          resolve({ data: null, error: { message: 'Supabase not configured' } }),
      }
      return updateBuilder
    },
    delete: () => {
      // Create chainable query builder for delete operations
      const deleteBuilder: Record<string, unknown> = {
        // Support chained .eq() calls
        eq: () => deleteBuilder,
        neq: () => deleteBuilder,
        in: () => deleteBuilder,
        is: () => deleteBuilder,
        like: () => deleteBuilder,
        // Direct resolution
        then: async (resolve: (arg0: { error: { message: string } }) => unknown) =>
          resolve({ error: { message: 'Supabase not configured' } }),
      }
      return deleteBuilder
    },
    upsert: () => ({
      select: () => ({
        single: async () => ({
          data: null,
          error: { message: 'Supabase not configured' },
        }),
      }),
    }),
  }),
  storage: {
    from: (_bucket: string) => ({
      upload: async () => ({
        data: null,
        error: { message: 'Storage not configured' },
      }),
      createSignedUrl: async () => ({
        data: null,
        error: { message: 'Storage not configured' },
      }),
      createSignedUploadUrl: async () => ({
        data: null,
        error: { message: 'Storage not configured' },
      }),
      getPublicUrl: () => ({ data: { publicUrl: '' } }),
      remove: async () => ({ error: { message: 'Storage not configured' } }),
      list: async () => ({ data: [], error: null }),
    }),
  },
  rpc: async () => ({ data: null, error: { message: 'RPC not configured' } }),
})

/**
 * Singleton browser client. Multiple GoTrue instances with the same storage
 * key contend for the same navigator LockManager lock and trigger
 * `NavigatorLockAcquireTimeoutError`, which prevents `INITIAL_SESSION` from
 * firing and leaves the auth context stuck in the loading state.
 */
let browserClientSingleton: ReturnType<typeof createBrowserClient> | null = null

/**
 * Create browser client for client-side usage
 *
 * @example
 * const supabase = createClient()
 * const { data, error } = await supabase.auth.signInWithPassword({ email, password })
 */
export function createClient() {
  if (!isSupabaseConfigured()) {
    logWarningOnce()
    return createStubClient() as unknown as ReturnType<typeof createBrowserClient>
  }

  if (typeof window === 'undefined') {
    // SSR/RSC should never reach the browser client; create a transient
    // instance rather than caching on the module (avoids cross-request leaks).
    return createBrowserClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      {
        auth: {
          // Use an in-process lock instead of the Navigator LockManager.
          // The navigator lock is origin-scoped and can deadlock across
          // tabs or after HMR reloads, preventing INITIAL_SESSION from
          // firing and leaving AuthProvider stuck in `isLoading: true`.
          // processLock is per-JS-context, which matches the singleton
          // browser client pattern and avoids cross-tab contention.
          lock: processLock,
        },
      }
    )
  }

  if (!browserClientSingleton) {
    browserClientSingleton = createBrowserClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      {
        auth: {
          // See comment above — avoids navigator-lock deadlocks that
          // block the GoTrue client's INITIAL_SESSION emission.
          lock: processLock,
        },
      }
    )
  }

  return browserClientSingleton
}

/**
 * Create server client for server-side usage (Server Components, Server Actions)
 *
 * @example
 * const supabase = await createServerSupabaseClient()
 * const { data: { user } } = await supabase.auth.getUser()
 */
export async function createServerSupabaseClient() {
  if (!isSupabaseConfigured()) {
    logWarningOnce()
    return createStubClient() as unknown as ReturnType<typeof createServerClient>
  }

  // Dynamic import to avoid client-side bundling of next/headers
  const { cookies } = await import('next/headers')
  const cookieStore = await cookies()

  // Use the public URL's storage key for consistency with browser client
  const publicStorageKey = getStorageKey()
  // Get internal URL's storage key (used by Supabase internally)
  const internalUrl = getServerSupabaseUrl()
  let internalStorageKey = 'sb-auth-token'
  try {
    const hostname = new URL(internalUrl).hostname
    internalStorageKey = `sb-${hostname}-auth-token`
  } catch {
    // Keep default
  }

  return createServerClient(
    getServerSupabaseUrl(),
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll() {
          // Read cookies - map internal key name to public key name
          const allCookies = cookieStore.getAll()
          return allCookies.map((cookie) => {
            // If this is a cookie with the public storage key, also make it available
            // under the internal key name for Supabase to read
            if (cookie.name === publicStorageKey) {
              return { ...cookie, name: internalStorageKey }
            }
            return cookie
          })
        },
        setAll(cookiesToSet) {
          try {
            cookiesToSet.forEach(({ name, value, options }) => {
              // When Supabase sets cookies with the internal key name,
              // also set them with the public key name for browser client
              if (name === internalStorageKey) {
                cookieStore.set(publicStorageKey, value, options)
              }
              // Also set with the original name
              cookieStore.set(name, value, options)
            })
          } catch {
            // The `setAll` method was called from a Server Component.
            // This can be ignored if you have middleware refreshing sessions.
          }
        },
      },
    }
  )
}

/**
 * Create admin client with service role key for admin operations
 * WARNING: Only use on server-side, never expose to client
 *
 * @example
 * const adminClient = createAdminClient()
 * const { data, error } = await adminClient.auth.admin.createUser({ email, password })
 */
export function createAdminClient() {
  const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY

  if (!isSupabaseConfigured() || !serviceRoleKey) {
    logWarningOnce()
    return createStubClient() as unknown as ReturnType<typeof createSupabaseClient>
  }

  return createSupabaseClient(getServerSupabaseUrl(), serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
  })
}

/**
 * Check if Supabase is available
 */
export function isSupabaseAvailable(): boolean {
  return isSupabaseConfigured()
}

/**
 * Type exports for use in other modules
 */
export type SupabaseClient = ReturnType<typeof createClient>
export type ServerSupabaseClient = Awaited<ReturnType<typeof createServerSupabaseClient>>
export type AdminSupabaseClient = ReturnType<typeof createAdminClient>
